import * as React from "react";
import Loadable from "react-loadable";
import { Route, Switch } from "react-router-dom";

import { IS_DEV, B2B_ENABLED } from "@config/consts";
import { GIFT_CARDS_LINK } from "@config/links";
import { ROUTES } from "@config/app.routing";

import { AVAILABLE_COUNTRIES, SERVICE_TYPE } from "@typings/globals";

import { PageSkeleton } from "@ui/Skeletons";

import { MakeRedirect } from "@containers/MakeRedirect";

import { useLocalisation } from "@hooks/useLocalisation";

import {
  AgentGuard,
  AuthGuard,
  MobileOnlyGuard,
  CountryGuard,
  ServiceTypeGuard,
  FeatureToggleGuard,
} from "@app/containers";
import {
  ClientDashboard,
  Booking,
  EndOfTenancy,
  B2B,
  NotFound,
  PaymentProcessed,
  Redirection,
  Apply,
  Support,
  UrssafRegistration,
} from "@app/pages";
import { GiftCardsSkeleton } from "@app/pages/GiftCards/GiftCards.skeleton";

const LoadableDevRedirect = Loadable({
  loader: () => import("../../__DEV/Dev.redirect"),
  render(loaded: any): JSX.Element {
    const Component = loaded.DevRedirect;
    return <Component />;
  },
  loading: () => null,
});

const LoadableDevSwitch = Loadable({
  loader: () => import("../../__DEV/Dev.switch"),
  render(loaded: any): JSX.Element {
    const Component = loaded.DevSwitch;
    return <Component />;
  },
  loading: () => null,
});

const LoadableWrongServiceType = Loadable({
  loader: () => import("../../pages/WrongServiceType"),
  render(loaded: any): JSX.Element {
    const Component = loaded.WrongServiceType;
    return <Component />;
  },
  loading: PageSkeleton,
});

const LoadableGiftCards = Loadable({
  loader: () => import("../../pages/GiftCards"),
  render(loaded: any): JSX.Element {
    const Component = loaded.GiftCards;
    return <Component />;
  },
  loading: GiftCardsSkeleton,
});

const AppSwitch = (): React.ReactElement => {
  const { language } = useLocalisation();

  return (
    <Switch>
      <Route
        exact
        path={ROUTES.BASE}
        render={() => (
          <React.Fragment>
            <MakeRedirect possibleRoutes={["client"]} />
            {IS_DEV && <LoadableDevRedirect />}
          </React.Fragment>
        )}
      />
      <Route
        path={ROUTES.CLIENT_DASHBOARD}
        render={props => (
          <AuthGuard redirect={ROUTES.REDIRECTION}>
            <ClientDashboard {...(props as any)} />
          </AuthGuard>
        )}
      />
      <Route
        path={ROUTES.BOOKING}
        render={props => (
          <AgentGuard>
            <ServiceTypeGuard
              serviceType={SERVICE_TYPE.HOME_CLEANING}
              {...props}
            >
              <Booking {...(props as any)} />
            </ServiceTypeGuard>
          </AgentGuard>
        )}
      />
      <Route
        path={ROUTES.END_OF_TENANCY}
        render={props => (
          <AgentGuard>
            <EndOfTenancy {...(props as any)} />
          </AgentGuard>
        )}
      />
      {B2B_ENABLED && (
        <Route
          path={ROUTES.B2B}
          render={props => (
            <AgentGuard>
              <CountryGuard
                whitelist={[
                  AVAILABLE_COUNTRIES.CH,
                  AVAILABLE_COUNTRIES.LU,
                  AVAILABLE_COUNTRIES.IT,
                ]}
              >
                <ServiceTypeGuard serviceType={SERVICE_TYPE.B2B} {...props}>
                  <B2B {...(props as any)} />
                </ServiceTypeGuard>
              </CountryGuard>
            </AgentGuard>
          )}
        />
      )}
      <Route path={ROUTES.PAYMENT_PROCESSED} component={PaymentProcessed} />
      <Route path={ROUTES.APPLY} component={Apply} />
      <Route
        path={ROUTES.SUPPORT}
        render={() => (
          <MobileOnlyGuard>
            <Support />
          </MobileOnlyGuard>
        )}
      />
      <Route
        path={ROUTES.URSSAF_REGISTRATION}
        render={() => (
          <AuthGuard>
            <UrssafRegistration />
          </AuthGuard>
        )}
      />
      <Route
        path={ROUTES.WRONG_SERVICE_TYPE}
        render={() => (
          <AuthGuard>
            <LoadableWrongServiceType />
          </AuthGuard>
        )}
      />
      <Route
        path={ROUTES.GIFT_CARDS}
        render={() => (
          <FeatureToggleGuard
            featureToggle="gift_cards"
            redirect={`/${language}${GIFT_CARDS_LINK}`}
            redirectWithReload
          >
            <LoadableGiftCards />
          </FeatureToggleGuard>
        )}
      />

      {IS_DEV && <LoadableDevSwitch />}

      <Route path={ROUTES.NOT_FOUND} component={NotFound} />
      <Route path={ROUTES.REDIRECTION} component={Redirection} />
      <Route component={NotFound} />
    </Switch>
  );
};

export default AppSwitch;
